/* eslint-disable camelcase */
// import json from data.json file
import querystring from "query-string";
import { IProduct } from '../entities/product/types';
import { getSectionFromUrl } from "../utilites/util";
import Consumers from "../constants/Consumers";

const config = {
  phone: {
    memory: {
      min: 2000, max: 8000,
    },
    screen: { min: 5, max: 6 }
  },
  tablet: {
    memory: { min: 2000, max: 32000, },
    screen: { min: 5, max: 6 }
  }
};

const getProductName = (product: IProduct) => product.manufacturer.name;
const getProductCategory = (product: IProduct) => product.productLine.name;

const filterKeys = {
  ch_operating_system: 'opsystems',
  ch_memory: 'ramMemory',
  ch_screen_size: 'screenSize',
  ch_esim: 'eSim',
  ch_dual_sim: 'dualSim'
} as const;

const extractFiltersFromProducts = (prod: IProduct[]) => {
  const urlParams = querystring.parse(window.location.search);
  const isBusiness = getSectionFromUrl() === Consumers.BUSINESS_CONSUMER;
  const filterList: Record<string, any> = {
    purchase: new Set(),
    tpackage: { cosmos: new Map(), subsidy: new Map() },
    cosmosInstallments: [],
    outlet: new Set(),
    category: new Set(),
    brand: new Set(),
    opsystems: new Set(),
    ramMemory: new Set(),
    screenSize: new Set(),
    dualSim: new Set(),
    eSim: new Set()
  };

  prod.forEach((product) => {
    filterList.brand.add(getProductName(product));
    filterList.outlet.add(product.outlet);
    filterList.category.add(getProductCategory(product));
    product.productCharacteristics.forEach((pc) => {
      const { characteristicIdentifier, value } = pc;
      if (Object.keys(filterKeys).includes(characteristicIdentifier)) {
        const mappedKey = filterKeys[characteristicIdentifier as keyof typeof filterKeys];
        filterList[mappedKey].add(value);
      }
    });
    // for Watches and Accessories
    if (product.tariffPackages.length === 0) {
      if ('cosmos' in product.productPrices) filterList.purchase.add('cosmos');
      if ('subsidy' in product.productPrices) {
        const discount = product.productPrices.subsidy?.discounts?.[0];
        let subsidyPrice = 'subsidy';
        if (discount?.value > 0) subsidyPrice = `subsidy-${discount.value}${discount.unit}`;
        filterList.purchase.add(subsidyPrice);
      }
    }
    // extracting cosmos installments
    if (!isBusiness) {
      const cosmosPricesTariffPackages  = product.tariffPackages.flatMap((elements) => elements.prices.cosmos);
      const installments = cosmosPricesTariffPackages.map(item => item.installments);
      // removing duplicate installments
      const uniqueInstallments = [...new Set(installments)];
      uniqueInstallments.forEach(el =>  filterList.cosmosInstallments.push(el));
    }

    // for other products and Myki watches
    product.tariffPackages.forEach(({ id, name, prices, cosmosSubscription, subscription, tariffPackageGroup }) => {
      if ('cosmos' in prices) {
        filterList.tpackage.cosmos.set(id, { name, price: cosmosSubscription, group: tariffPackageGroup?.name });
        filterList.purchase.add('cosmos');
      }
      if ('subsidy' in prices) {
        filterList.tpackage.subsidy.set(id, { name, price: subscription, group: tariffPackageGroup?.name });
        filterList.purchase.add('subsidy');
      }
    });
  });

  return filterList;
};

const memoryArrayConstructor = (arr: any, configObj: any) => {
  const formatedMemory = new Set();
  const memoryArray = Array.from(arr).map(v => Number(v))
    .sort((a, b) => a - b);
  memoryArray.forEach((ram) => {
    if (ram <= configObj.min) {
      formatedMemory.add(`< ${configObj.min / 1000} GB`);
    }
    if (ram > configObj.min && ram < configObj.max) {
      formatedMemory.add(`${(ram / 1000).toString()} GB`);
    }
    if (ram >= configObj.max) {
      formatedMemory.add(`> ${configObj.max / 1000} GB`);
    }
  });
  return Array.from(formatedMemory);
};

const screenSizeArrayConstructor = (arr: any, configObj: any) => {
  const formatedScreenSize = new Set();
  const sizeArray = Array.from(arr).map(v => Number(v))
    .sort((a, b) => a - b);
  sizeArray.forEach((size) => {
    if (size <= configObj.min) {
      formatedScreenSize.add(`< ${configObj.min.toString()}"`);
    }
    if (size > configObj.min && size < configObj.max) {
      formatedScreenSize.add(`${configObj.min.toString()}" < ${configObj.max.toString()}"`);
    }
    if (size >= configObj.max) {
      formatedScreenSize.add(`${configObj.max.toString()}" >`);
    }
  });
  return Array.from(formatedScreenSize);
};

const transformFilterSetToArray = (filterList: any, configObj: any) => {
  return Object.keys(filterList).reduce((acc, key) => {
    if (key === 'ramMemory' && configObj?.memory) {
      const memoryArray = memoryArrayConstructor(filterList[key], configObj.memory);
      return {
        ...acc,
        [key]: memoryArray
      };
    }
    if (key === 'screenSize' && configObj?.screen) {
      const screenSizeArray = screenSizeArrayConstructor(filterList[key], configObj.screen);
      return {
        ...acc,
        [key]: screenSizeArray
      };
    }
    if (key === 'tpackage') {
      return {
        ...acc,
        [key]: filterList[key]
      };
    }
    return {
      ...acc,
      [key]: Array.from(filterList[key]).sort((a: any, b: any) => {
        return a > b ? 1
          : a === b ? 0
            : -1;
      }),
    };
  }, {});
};

export const getFiltersFromProducts = (prod: IProduct[], category = 'phone') => {
  const configObj = config[category as keyof typeof config] ?? null;
  const filterList = extractFiltersFromProducts(prod);

  return transformFilterSetToArray(filterList, configObj);
};


